// 214 §2–§4 [RULED 51–53] — WHAT A COUNSEL'S CHIP CARRIES beside its name and roles, in the parties pane of a case
// and of a collective case (MultiSelect's `chipExtra` slot, 141 §2.2's render prop): the parties he stands for
// (CounselPartyPicker), and the link's state — acting, with « Suspendre » when unlocked; or « suspendu depuis le
// JJ/MM/AA », greyed, with « Réactiver » when unlocked. The same slot serves a client's own counsel (a contact with
// the role Avocat): the role's word as a tag, the state, no party picker (he stands for his client).
//
// Hookless functions returning JSX, called from the two views' `chipExtra` callbacks — never mounted as JSX from a
// render body (scripts/lint-inline-components.js); the one stateful piece inside is CounselPartyPicker, a
// module-scope component.
/* `import React`: react/react-in-jsx-scope is counted per JSX element and this is the rule's own remedy (189 §E). */
import React from "react";
import CounselPartyPicker, { type PartyOption } from "./CounselPartyPicker";
import { FS, RAD } from "../lib/tokens";
import { isSuspended, partyIdsOf, shortDate, suspendedDateOf, type CounselLink, type LinkState } from "../lib/counselLinks";
import type { TFn } from "../lib/taxonomyTypes";

interface StateProps {
  readonly link: LinkState | null | undefined;
  readonly locked: boolean;
  readonly t: TFn;
  readonly onSuspend: () => void;
  readonly onReactivate: () => void;
}

/** The state pill and its one action (an empty fragment when the link is acting and locked). */
export function linkStateExtra({ link, locked, t, onSuspend, onReactivate }: StateProps): React.ReactElement {
  const btn = (label: string, onClick: () => void, mark: string) => (
    <button
      type="button"
      className="btn btn-ghost"
      data-link-action={mark}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{ padding: "0 5px", fontSize: FS.s8, height: 15, lineHeight: "13px", borderRadius: RAD.r4 }}
    >
      {label}
    </button>
  );
  if (isSuspended(link)) {
    const since = shortDate(suspendedDateOf(link));
    return (
      <>
        <span
          data-link-state="suspended"
          style={{ fontSize: FS.s8, color: "var(--tm)", fontStyle: "italic", background: "var(--bi)", borderRadius: RAD.r3, padding: "0 4px", lineHeight: "13px" }}
        >
          {t("link.suspended_since", { date: since, fallback: `suspended since ${since}` })}
        </span>
        {!locked && btn(t("link.action.reactivate", { fallback: "Reactivate" }), onReactivate, "reactivate")}
      </>
    );
  }
  return <>{!locked && btn(t("co.action.suspend", { fallback: "Suspend" }), onSuspend, "suspend")}</>;
}

interface CounselProps extends StateProps {
  readonly link: CounselLink | null | undefined;
  readonly parties: readonly PartyOption[];
  readonly onParties: React.Dispatch<number[]>;
}

/** A counsel's chip: the party picker, then the state. */
export function counselChipExtra(p: CounselProps): React.ReactElement {
  return (
    <>
      <CounselPartyPicker partyIds={partyIdsOf(p.link)} parties={p.parties} onChange={p.onParties} disabled={p.locked} />
      {linkStateExtra(p)}
    </>
  );
}

interface ContactProps extends StateProps {
  /** The role's word (the taxonomy label of `lawyer`), shown as a tag. */
  readonly roleLabel: string;
}

/** A client's own counsel's chip (a contact with the role Avocat): the role tag, then the state. */
export function clientCounselChipExtra(p: ContactProps): React.ReactElement {
  return (
    <>
      <span data-contact-role="lawyer" style={{ fontSize: FS.s8, background: "var(--bi)", color: "var(--ts)", borderRadius: RAD.r3, padding: "0 4px", lineHeight: "13px" }}>
        {p.roleLabel}
      </span>
      {linkStateExtra(p)}
    </>
  );
}
